/**
 * EVENTSFLO-GENERIC-FINANCIAL-CREDIT-ADJUSTMENT-LINE-V1
 *
 * Quote-level generic Credit / Adjustment panel.
 *
 * Renders next to the existing "Add service credit" area on
 * QuoteDetailPage. Draft-only mutations. Financial-only — never
 * creates or references a Shift. Adjustments live in a top-level
 * `quote_adjustments` collection on the backend and survive quote
 * regeneration.
 */
import { useCallback, useEffect, useState } from "react";
import { api, extractErrorMessage } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Trash2, Plus, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { DestructiveConfirmDialog } from "../shared/DestructiveConfirmDialog";

const ADJUSTMENT_TYPE_OPTIONS = [
  { value: "commercial_gesture", label: "Commercial Gesture" },
  { value: "credit", label: "Credit" },
  { value: "discount", label: "Discount" },
  { value: "correction", label: "Correction" },
  { value: "payback", label: "Payback" },
  { value: "other", label: "Other" },
];

const _fmt = (val, currencySymbol) =>
  `${currencySymbol}${(Number(val) || 0).toFixed(2)}`;

const _emptyForm = () => ({
  adjustment_type: "commercial_gesture",
  description: "",
  quantity: "",
  units: "",
  unit_rate: "",
  amount: "",
  date: "",
  time: "",
  internal_note: "",
});

export default function QuoteAdjustmentsPanel({
  quoteId,
  quoteStatus,
  currencySymbol = "€",
  onChange,
}) {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(_emptyForm());
  // EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
  // Confirmation state for the destructive Remove action + editing
  // state for the new inline Edit flow.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);  // null = "add mode"

  const isDraft = (quoteStatus || "draft").toLowerCase() === "draft";

  const load = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const r = await api.get(`/quotes/${quoteId}/adjustments`);
      setAdjustments(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to load adjustments");
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  // EVENTSFLO-GENERIC-FINANCIAL-CREDIT-ADJUSTMENT-LINE-V1
  // If quantity + units + unit_rate are all present, auto-fill the
  // amount for the user. They can still override the amount field
  // afterwards — the SERVER treats amount as the source of truth for
  // totals, this is a UX convenience only.
  const computedAmount = (() => {
    const q = parseFloat(form.quantity);
    const u = parseFloat(form.units);
    const r = parseFloat(form.unit_rate);
    if (Number.isFinite(q) && Number.isFinite(u) && Number.isFinite(r)) {
      return Number((q * u * r).toFixed(2));
    }
    return null;
  })();

  const openAddDialog = () => {
    setEditingId(null);
    setForm(_emptyForm());
    setDialogOpen(true);
  };

  // EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
  // Populate the same dialog from an existing row for the Edit flow.
  // Backend PATCH endpoint /api/quote-adjustments/{id} accepts partial
  // updates and enforces the same draft-only guard as create.
  const openEditDialog = (adj) => {
    setEditingId(adj.id);
    setForm({
      adjustment_type: adj.adjustment_type || "commercial_gesture",
      description: adj.description || "",
      quantity: adj.quantity == null ? "" : String(adj.quantity),
      units: adj.units == null ? "" : String(adj.units),
      unit_rate: adj.unit_rate == null ? "" : String(adj.unit_rate),
      amount: adj.amount == null ? "" : String(adj.amount),
      date: adj.date || "",
      time: adj.time || "",
      internal_note: adj.internal_note || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    // Effective amount: prefer typed value; otherwise use computed.
    let amount = parseFloat(form.amount);
    if (!Number.isFinite(amount) && computedAmount !== null) {
      amount = computedAmount;
    }
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error("Enter an amount (negative for a credit) or fill quantity × units × unit rate");
      return;
    }
    const payload = {
      adjustment_type: form.adjustment_type,
      description: form.description.trim(),
      amount,
      quantity: form.quantity === "" ? null : Number(form.quantity),
      units: form.units === "" ? null : Number(form.units),
      unit_rate: form.unit_rate === "" ? null : Number(form.unit_rate),
      date: form.date || null,
      time: form.time || null,
      internal_note: form.internal_note || null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/quote-adjustments/${editingId}`, payload);
        toast.success("Credit / adjustment updated");
      } else {
        await api.post(`/quotes/${quoteId}/adjustments`, payload);
        toast.success("Credit / adjustment added");
      }
      setDialogOpen(false);
      setEditingId(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(extractErrorMessage(err) || (editingId ? "Failed to update adjustment" : "Failed to add adjustment"));
    } finally {
      setSaving(false);
    }
  };

  // EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
  // Two-step Remove flow. Row-level Trash2 opens the confirmation
  // dialog; the confirmation triggers the actual DELETE. Kept draft-
  // only client-side; backend independently enforces draft-only 409.
  const requestDelete = (id) => {
    if (!isDraft) return;
    setPendingDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/quote-adjustments/${pendingDeleteId}`);
      toast.success("Credit / adjustment removed");
      setPendingDeleteId(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to remove credit / adjustment");
    } finally {
      setDeleting(false);
    }
  };

  const typeLabel = (v) =>
    ADJUSTMENT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v;

  return (
    <div className="space-y-3" data-testid="quote-adjustments-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Credits & Adjustments</h3>
          <p className="text-xs text-muted-foreground">
            Generic quote-level credits (commercial gesture, discount, van
            rental credit, correction, payback…). Reduces the quote total.
            Does not create shifts or crew demand.
          </p>
        </div>
        {isDraft && (
          <Button
            variant="outline"
            size="sm"
            onClick={openAddDialog}
            data-testid="quote-add-adjustment-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Credit / Adjustment
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Loading…</div>
      ) : adjustments.length === 0 ? (
        <div
          className="text-xs text-muted-foreground italic py-2"
          data-testid="quote-adjustments-empty"
        >
          No credits or adjustments yet.
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="quote-adjustments-table">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {adjustments.map((a) => (
                <tr
                  key={a.id}
                  data-testid={`quote-adjustment-row-${a.id}`}
                  className="border-t border-border"
                >
                  <td className="px-3 py-2">{typeLabel(a.adjustment_type)}</td>
                  <td className="px-3 py-2 max-w-[380px] break-words">
                    {a.description}
                  </td>
                  <td className="px-3 py-2">
                    {a.date || "—"}
                    {a.time ? ` ${a.time}` : ""}
                  </td>
                  <td
                    className="px-3 py-2 text-right font-medium"
                    style={{ color: Number(a.amount) < 0 ? "#b00020" : "inherit" }}
                    data-testid={`quote-adjustment-amount-${a.id}`}
                  >
                    {Number(a.amount) < 0 ? "-" : ""}
                    {_fmt(Math.abs(a.amount), currencySymbol)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isDraft && (
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(a)}
                          data-testid={`quote-adjustment-edit-${a.id}`}
                          title="Edit credit / adjustment"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => requestDelete(a.id)}
                          data-testid={`quote-adjustment-delete-${a.id}`}
                          title="Remove credit / adjustment"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingId(null); }}>
        <DialogContent className="max-w-2xl" data-testid="quote-adjustment-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Credit / Adjustment" : "Add Credit / Adjustment"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="adj-type">Type</Label>
              <Select
                value={form.adjustment_type}
                onValueChange={(v) => setForm({ ...form, adjustment_type: v })}
              >
                <SelectTrigger id="adj-type" data-testid="quote-adjustment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-date">Date (optional)</Label>
              <Input
                id="adj-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="quote-adjustment-date-input"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="adj-desc">Description *</Label>
              <Textarea
                id="adj-desc"
                rows={2}
                placeholder="e.g. Credit for 4 Crew for 2 hours — payback for absent Crew"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="quote-adjustment-description-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-time">Time (optional)</Label>
              <Input
                id="adj-time"
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                data-testid="quote-adjustment-time-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-qty">Quantity (optional)</Label>
              <Input
                id="adj-qty"
                type="number"
                step="0.01"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="quote-adjustment-qty-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-units">Units / Hours (optional)</Label>
              <Input
                id="adj-units"
                type="number"
                step="0.01"
                value={form.units}
                onChange={(e) => setForm({ ...form, units: e.target.value })}
                data-testid="quote-adjustment-units-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-rate">Unit Rate (optional; negative = credit)</Label>
              <Input
                id="adj-rate"
                type="number"
                step="0.01"
                value={form.unit_rate}
                onChange={(e) => setForm({ ...form, unit_rate: e.target.value })}
                data-testid="quote-adjustment-rate-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="adj-amount">
                Amount ({currencySymbol}) *{" "}
                <span className="text-xs text-muted-foreground">
                  negative for credit
                </span>
              </Label>
              <Input
                id="adj-amount"
                type="number"
                step="0.01"
                placeholder={computedAmount !== null ? String(computedAmount) : "e.g. -294.00"}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                data-testid="quote-adjustment-amount-input"
              />
              {computedAmount !== null && (
                <p className="text-xs text-muted-foreground" data-testid="quote-adjustment-computed-hint">
                  Computed from qty × units × rate: {_fmt(computedAmount, currencySymbol)}
                </p>
              )}
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label htmlFor="adj-note">Internal note (optional)</Label>
              <Textarea
                id="adj-note"
                rows={2}
                value={form.internal_note}
                onChange={(e) => setForm({ ...form, internal_note: e.target.value })}
                data-testid="quote-adjustment-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
              data-testid="quote-adjustment-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="quote-adjustment-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Saving…
                </>
              ) : (
                editingId ? "Save Changes" : "Save Credit / Adjustment"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-QUOTES-SEARCH-AND-ADJUSTMENT-REMOVE-UX-V1
          Two-step Remove confirmation. Uses the shared destructive
          dialog so wording, style and keyboard behaviour match the
          rest of the app. */}
      <DestructiveConfirmDialog
        open={Boolean(pendingDeleteId)}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}
        title="Remove credit / adjustment?"
        itemType="credit / adjustment"
        description="Remove this credit / adjustment from the quote? The line will be deleted and the quote total will update. Shifts, crew and other line items are not affected."
        confirmText={deleting ? "Removing…" : "Remove"}
        onConfirm={confirmDelete}
        loading={deleting}
      />
    </div>
  );
}
